<template>
  <transition name="refresh-pill">
    <div
      v-if="isVisible"
      class="refresh-pill"
      role="status"
      aria-live="polite"
    >
      <span :class="['refresh-dot', refreshStatus]" />
      <span>{{ label }}</span>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useLoadingStore } from '@/stores/loading'
import { useAuthStore } from '@/stores/auth'

const loadingStore = useLoadingStore()
const authStore = useAuthStore()

const refreshStatus = computed(() => loadingStore.refreshDataStatus)
const isVisible = computed(
  () => authStore.isLoggedIn && refreshStatus.value !== 'idle',
)

const label = computed(() => {
  if (refreshStatus.value === 'success') return 'Data refreshed'
  if (refreshStatus.value === 'error') return 'Refresh issue'
  return 'Refreshing data'
})
</script>

<style scoped>
.refresh-pill {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 10px);
  left: 50%;
  z-index: 10000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 138px;
  padding: 7px 12px;
  border: 1px solid var(--ion-color-gray3);
  border-radius: 999px;
  background: rgba(var(--ion-color-gray0-rgb), 0.96);
  color: var(--ion-text-color);
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 8px 24px rgba(var(--ion-color-dark-rgb), 0.14);
  transform: translateX(-50%);
}

.refresh-dot {
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.refresh-dot::after {
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  content: '';
}

.refresh-dot.refreshing {
  background: var(--ion-color-warning);
}

.refresh-dot.refreshing::after {
  background: rgba(var(--ion-color-warning-rgb), 0.35);
  animation: refresh-pulse 1.4s ease-out infinite;
}

.refresh-dot.success {
  background: var(--ion-color-success);
}

.refresh-dot.error {
  background: var(--ion-color-danger);
}

.refresh-dot.success::after,
.refresh-dot.error::after {
  display: none;
}

.refresh-pill-enter-active,
.refresh-pill-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.refresh-pill-enter-from,
.refresh-pill-leave-to {
  opacity: 0;
  transform: translate(-50%, -8px);
}

@keyframes refresh-pulse {
  0% {
    opacity: 0.85;
    transform: scale(0.45);
  }

  100% {
    opacity: 0;
    transform: scale(1.4);
  }
}
</style>
